import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Bank } from '@phosphor-icons/react';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { formatDateDMY } from '../utils/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const L = (lang, el, en) => (lang === 'el' ? el : en);
const eur = (v) => `${Number(v) < 0 ? '-' : ''}€${Math.abs(Number(v || 0)).toLocaleString('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const SORTS = [
  ['oldest', 'Παλαιότερη καταχώρηση τράπεζας', 'Oldest bank entry'], ['newest', 'Νεότερη καταχώρηση τράπεζας', 'Newest bank entry'],
  ['high', 'Υψηλότερο υπόλοιπο τράπεζας', 'Highest bank balance'], ['low', 'Χαμηλότερο υπόλοιπο τράπεζας', 'Lowest bank balance'], ['name', 'Όνομα Α–Ω', 'Name A–Z'],
];
const num = (v, dflt) => (v == null ? dflt : v);
const SORT_FN = {
  oldest: (a, b) => String(a.last_bank_date || '').localeCompare(String(b.last_bank_date || '')),
  newest: (a, b) => String(b.last_bank_date || '').localeCompare(String(a.last_bank_date || '')),
  high: (a, b) => num(b.bank_balance, -Infinity) - num(a.bank_balance, -Infinity),
  low: (a, b) => num(a.bank_balance, Infinity) - num(b.bank_balance, Infinity),
  name: (a, b) => a.building_name.localeCompare(b.building_name),
};

const Money = ({ v, lang, testid }) => (v == null
  ? <span className="text-zinc-400 italic" data-testid={testid}>{L(lang, 'Μη διαθέσιμο υπόλοιπο', 'Balance Unavailable')}</span>
  : <span className={`font-mono ${v < 0 ? 'text-red-700' : ''}`} data-testid={testid}>{eur(v)}</span>);
const DateCell = ({ d, lang, testid }) => (d
  ? <span className="font-mono" data-testid={testid}>{formatDateDMY(String(d).slice(0, 10))}</span>
  : <span className="text-zinc-400 italic" data-testid={testid}>{L(lang, 'Χωρίς εγγραφές', 'No Entries')}</span>);

const Summary = ({ label, value, unavailable, lang, testid }) => (
  <div className="border border-zinc-200 bg-white px-4 py-3 min-w-[220px]" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className="text-2xl font-bold font-mono mt-1">{eur(value)}</div>
    {unavailable > 0 && <div className="text-[11px] text-zinc-400">{L(lang, `${unavailable} κτίρια χωρίς διαθέσιμο υπόλοιπο`, `${unavailable} buildings without available balance`)}</div>}
  </div>
);

export default function BankReceipts() {
  const { language } = useLanguage();
  const lang = language === 'en' ? 'en' : 'el';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [sort, setSort] = useState('oldest');
  const load = useCallback((refresh = false) => {
    setError('');
    axios.get(`${API}/dashboard/bank-receipts/balances${refresh ? '?refresh=true' : ''}`, { withCredentials: true, timeout: 120000 })
      .then((r) => {
        setData(r.data);
        if (r.data.stale) setTimeout(() => load(), 25000);
      })
      .catch((e) => setError(e.response?.data?.detail || e.message || 'Error'));
  }, []);
  useEffect(() => {
    load();
    const onFocus = () => document.visibilityState === 'visible' && load();
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  }, [load]);
  const rows = useMemo(() => [...(data?.buildings || [])].sort((a, b) => SORT_FN[sort](a, b) || a.building_name.localeCompare(b.building_name)), [data, sort]);
  const t = data?.totals || {};

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="bank-receipts-page">
        <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}><Bank size={22} />Bank Receipts</h1>
        {error && (
          <div className="mt-4 border border-red-300 bg-red-50 text-red-800 text-sm px-4 py-3 flex items-center gap-3" data-testid="br-error">
            {L(lang, 'Δεν ήταν δυνατή η φόρτωση των υπολοίπων — τα στοιχεία δεν εμφανίζονται ως €0,00.', 'Balances could not be loaded — values are not shown as €0.00.')} ({error})
            <button type="button" className="underline font-semibold" onClick={() => load()} data-testid="br-retry">{L(lang, 'Επανάληψη', 'Retry')}</button>
          </div>)}
        {data && (
          <p className="text-[11px] text-zinc-500 mt-2" data-testid="br-computed-at">
            {L(lang, 'Υπολογίστηκε', 'Computed')}: {new Date(data.computed_at).toLocaleString('el-GR')}{data.stale ? ` · ${L(lang, 'ενημερώνεται…', 'updating…')}` : ''}
            <button type="button" className="underline ml-2" onClick={() => { setData(null); load(true); }} data-testid="br-refresh">{L(lang, 'Ανανέωση τώρα', 'Refresh now')}</button>
          </p>)}
        {data && <div className="flex flex-wrap gap-3 mt-3 mb-4">
          <Summary label={L(lang, 'Συνολικό Υπόλοιπο Τράπεζας', 'Total Bank Balance')} value={t.bank} unavailable={t.bank_unavailable} lang={lang} testid="br-total-bank" />
          <Summary label={L(lang, 'Συνολικό Υπόλοιπο Μετρητών', 'Total Cash Balance')} value={t.cash} unavailable={t.cash_unavailable} lang={lang} testid="br-total-cash" />
        </div>}
        <div className="bg-white border border-zinc-200">
          <div className="px-4 py-2 border-b border-zinc-200 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{L(lang, 'Ταξινόμηση', 'Sort')}</span>
            {SORTS.map(([k, el, en]) => (
              <button key={k} type="button" onClick={() => setSort(k)} data-testid={`br-sort-${k}`}
                className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 border transition-colors ${sort === k ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}>{L(lang, el, en)}</button>
            ))}
          </div>
          <table className="w-full text-sm" data-testid="br-table">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-600">
                <th className="px-4 py-2 font-semibold">{L(lang, 'Κτίριο', 'Building')}</th>
                <th className="px-3 py-2 font-semibold text-right">{L(lang, 'Υπόλοιπο Τράπεζας', 'Bank Balance')}</th>
                <th className="px-3 py-2 font-semibold">{L(lang, 'Τελευταία Καταχώρηση Τράπεζας', 'Last Bank Entry')}</th>
                <th className="px-3 py-2 font-semibold text-right">{L(lang, 'Υπόλοιπο Μετρητών', 'Cash Balance')}</th>
                <th className="px-3 py-2 font-semibold">{L(lang, 'Τελευταία Καταχώρηση Μετρητών', 'Last Cash Entry')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {!data && !error && <tr><td colSpan={5} className="px-4 py-6 text-center text-zinc-400" data-testid="br-loading">{L(lang, 'Υπολογισμός υπολοίπων από τα βιβλία Ταμείου/Τράπεζας κάθε κτιρίου… (έως 30″)', 'Computing balances from each building’s Cash/Bank ledger… (up to 30s)')}</td></tr>}
              {rows.map((b) => (
                <tr key={b.building_id} className="hover:bg-zinc-50" data-testid={`br-row-${b.building_id}`}>
                  <td className="px-4 py-2 font-medium"><Link to={`/buildings/${b.building_id}`} className="hover:underline">{b.building_name}</Link></td>
                  <td className="px-3 py-2 text-right"><Money v={b.bank_balance} lang={lang} testid={`br-bank-${b.building_id}`} /></td>
                  <td className="px-3 py-2"><DateCell d={b.last_bank_date} lang={lang} testid={`br-bank-date-${b.building_id}`} /></td>
                  <td className="px-3 py-2 text-right"><Money v={b.cash_balance} lang={lang} testid={`br-cash-${b.building_id}`} /></td>
                  <td className="px-3 py-2"><DateCell d={b.last_cash_date} lang={lang} testid={`br-cash-date-${b.building_id}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
